import { createRef, useEffect, useState } from "react";
import useAlert from "../../../Hooks/useAlert";
import useFullLoader from "../../../Hooks/useFullLoader";
import { AuthService } from "../../../services/AuthService";
import { ContentService } from "../../../services/ContentService";
import { SkillService } from "../../../services/SkillService";
import { SortString } from "../../../helpers/SortString";
import { SubSectorType } from "../../../enums/SubSectorType";
import { navigate } from "@reach/router";
import { getErrorInfo } from "../../../helpers/CommonHelper";
import { AlertType } from "../../../enums/AlertType";
import usePopUp from "../../../Hooks/usePopUp";
import { Modes } from "../../../enums/Modes";

const useClientManagement = ({ mode, id }) => {
  const { showAlert } = useAlert();
  const { setFullLoader } = useFullLoader();
  const formikRef = createRef(null);
  const { popUp } = usePopUp();

  const [clientDetail, setClientDetail] = useState(null);
  const [clientOrgs, setClientOrgs] = useState([]);
  const [clientInstanceRegion, setClientInstanceRegion] = useState([]);
  const [contentProviders, setContentProviders] = useState([]);
  const [subSectors, setSubSectors] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [sourceMenu, setSourceMenu] = useState([]);

  useEffect(() => {
    getOrgAndContentProviders();
  }, []);

  useEffect(() => {
    if (mode === Modes.EDIT) {
      getDetail();
    }

    return () => {
      setClientDetail(null);
    };
  }, [mode, id]);

  const getOrgAndContentProviders = async () => {
    setFullLoader(true);
    try {
      const resOrgs = await AuthService.getAllClientsOrgStageV3();
      const resInstanceRegion = await AuthService.getClientsInstanceRegionV3();
      const resContentPros = await ContentService.getAllContentProvidersV3();
      const resSource = await SkillService.getSkillSourcesV3();
      setSourceMenu(
        SortString(resSource, "source").map((el) => ({
          label: el.source,
          value: el.sourceId,
          allowMultipleMappings: el.allowMultipleMappings,
        }))
      );
      if (mode === Modes.ADD) {
        fetchSubSectors();
      }
      const sortedOrgs = SortString(resOrgs, "attributeValueId");
      setClientOrgs(
        sortedOrgs.map((el) => ({
          label: el.attrivuteValueName,
          value: el.attributeValueId,
        }))
      );
      const sortedRegion = SortString(resInstanceRegion, "attrivuteValueName");
      setClientInstanceRegion(
        sortedRegion.map((el) => ({
          label: el.attrivuteValueName,
          value: el.attributeValueId,
        }))
      );
      const filterContent =
        resContentPros?.filter((el) => !!el.contentProvider) || [];
      const sortedPros = SortString(filterContent, "contentProvider");
      setContentProviders(
        sortedPros.map((el) => ({
          label: el.contentProvider,
          value: el.contentProviderId,
        }))
      );

      setFullLoader(false);
    } catch (error) {
      console.log(error);
    } finally {
      setFullLoader(false);
    }
  };

  const getMappedList = (data) => {
    return data?.map((el) => ({
      label: el.subsector,
      value: el.subsectorId,
      sourceIds: el.sourceIds,
    }));
  };

  const fetchSubSectors = async (id = []) => {
    const sourceIdsParam = !!id.length
      ? "?" + id?.map((el) => `sourceIds=${el}`).join("&")
      : "";
    const resSubSectors = await AuthService.subSectorListV3(sourceIdsParam);
    const sortedSubSectors = SortString(
      resSubSectors?.filter(
        (el) => el.subsectorTypeId === SubSectorType.SUBSECTOR
      ),
      "subsector"
    );
    const sortedFunctions = SortString(
      resSubSectors?.filter(
        (el) => el.subsectorTypeId === SubSectorType.FUNCTIONS
      ),
      "subsector"
    );
    setFunctions(getMappedList(sortedFunctions));
    setSubSectors(getMappedList(sortedSubSectors));
  };

  const getDetail = async () => {
    try {
      const res = await AuthService.getClientDetailV3(Number(id));
      setClientDetail(res);
      if (mode === Modes.EDIT) {
        fetchSubSectors(res.sourceList?.map((el) => el.sourceId) || []);
      }
    } catch (error) {
      showAlert(error.error, "error", 1500);
      console.log(error);
    } finally {
    }
  };

  const processSubSectors = (subSectorType, clientDetail) => {
    return clientDetail?.subSectorList
      ?.map((el) => {
        if (el.subsectorTypeId === subSectorType) {
          return {
            label: el.subsector,
            value: el.subsectorId,
            sourceIds: el.sourceIds,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const getSubSectorIdList = (data) => {
    return data?.subSectorIdList
      ?.map(({ value }) => value)
      .concat(data?.functionIdList?.map(({ value }) => value));
  };

  const removeChip = (item, list, name, setFieldValue, formValues) => {
    setFieldValue(
      name,
      list.filter((el) => el.value !== item.value)
    );
    if (name === "sourceIdList") {
      const valueList = list.filter((el) => el.value !== item.value);
      setFieldValue(
        "subSectorIdList",
        valueList?.length > 0
          ? formValues?.subSectorIdList?.filter((el) =>
              el.sourceIds?.some((elem) => elem !== item.value)
            )
          : []
      );
      setFieldValue(
        "functionIdList",
        valueList?.length > 0
          ? formValues?.functionIdList?.filter((el) =>
              el.sourceIds?.some((elem) => elem !== item.value)
            )
          : []
      );
      fetchSubSectors(valueList?.map(({ value }) => value));
    }
  };

  const handleSearch = (
    item,
    reason,
    list,
    name,
    setFieldValue,
    setTouched
  ) => {
    if (!!item && item?.hasOwnProperty("allowMultipleMappings")) {
      if (!list?.some((el) => el.value === item.value)) {
        const valueList = item.allowMultipleMappings
          ? [...list, item].filter((el) => el.allowMultipleMappings === item.allowMultipleMappings)
          : [...list, item].filter(
              (el) => el.allowMultipleMappings === item.allowMultipleMappings && el.value === item.value
            );
        setFieldValue(name, reason === "select-option" ? valueList : list);
        if (name === "sourceIdList") {
          if (valueList?.length === 1) {
            setFieldValue("subSectorIdList", []);
            setFieldValue("functionIdList", []);
          }
          fetchSubSectors(valueList?.map(({ value }) => value));
        }
      } else {
        showAlert("This item already added", "info", 1500);
      }
    } else {
      handleSelectValue(item, reason, list, name, setFieldValue, setTouched);
    }
  };

  const handleSelectValue = (
    item,
    reason,
    list,
    name,
    setFieldValue,
    setTouched
  ) => {
    if (reason === "select-option") {
      if (!list?.some((el) => el.value === item.value)) {
        setFieldValue(
          name,
          reason === "select-option" ? [...list, item] : list
        );
      } else {
        showAlert("This item already added", "info", 1500);
      }
    }
  };

  const handleSelectAll = (list, selectAll, name, setFieldValue) => {
    setFieldValue(name, !selectAll ? list : []);
  };

  const handleUpdateYes = async (values) => {
    const {
      sourceIdList,
      subSectorIdList,
      contentProviderList,
      orgGrowthStageId,
      region,
      ...postValues
    } = values;

    let payload = {
      sourceIdList: values?.sourceIdList?.map(({ value }) => value),
      subSectorIdList: getSubSectorIdList(values),
      contentProviderIdList: values?.contentProviderList?.map(
        ({ value }) => value
      ),
      orgGrowthStageId: !!orgGrowthStageId ? orgGrowthStageId.value : 0,
      region: !!region ? region.value : 0,
      DisprzRegion: region?.label || "",
      ...postValues,
    };
    setFullLoader(true);
    try {
      await AuthService.updateClientV3("/" + Number(id), payload);
      await showAlert("Client Updation Successful", "success", 1500);
      setFullLoader(false);
      navigate("/clients");
    } catch (errors) {
      let error = JSON.parse(errors);
      showAlert(getErrorInfo(error), AlertType.ERROR, 1500);
    } finally {
      setFullLoader(false);
    }
  };

  const handleDeleteYes = async () => {
    setFullLoader(true);
    try {
      await AuthService.deleteClientV3("/" + Number(clientDetail.clientId));

      await showAlert("Client Deletion Successful", "success", 1500);
      await setFullLoader(false);
      setTimeout(() => navigate("/clients"), 100);
    } catch (errors) {
      showAlert(errors.error, "error", 1500);
    } finally {
      setFullLoader(false);
    }
  };

  const deleteTrigger = () => {
    popUp.confirm(
      ``,
      `Delete Client - ${formikRef.current.values.clientName}`,
      handleDeleteYes
    );
  };

  const handleBack = () => {
    navigate("/clients");
  }

  const handleCheckBack = () => {
    const hasFormChange = !!Object.keys(formikRef.current.touched).length;
    if (hasFormChange) {
      popUp.confirm(``, `Proceed without saving changes?`, handleBack);
    } else {
      handleBack();
    }
  };

  const formSubmit = async (fieldValues) => {
    setFullLoader(true);
    const {
      sourceIdList,
      subSectorIdList,
      contentProviderList,
      orgGrowthStageId,
      region,
      ...postValues
    } = fieldValues;

    try {
      let payload = {
        sourceIdList: sourceIdList?.map(({ value }) => value) || [],
        subSectorIdList: getSubSectorIdList(fieldValues),
        contentProviderIdList:
          fieldValues?.contentProviderList?.map(({ value }) => value) || [],
        orgGrowthStageId: !!orgGrowthStageId ? orgGrowthStageId?.value : 0,
        region: !!region ? region.value : 0,
        DisprzRegion: region?.label || "",
        ...postValues,
      };

      if (mode === Modes.ADD) {
        await AuthService.addClientV3(payload);
        await showAlert("Client Creation Successful", "success", 1500);
        await setFullLoader(false);
        navigate("/clients");
      } else {
        await setFullLoader(false);
        popUp.confirm(``, `Confirm Client Updation`, () => {handleUpdateYes(fieldValues)});
      }
    } catch (errors) {
      let error = JSON.parse(errors);
      showAlert(getErrorInfo(error), AlertType.ERROR, 1500);
    } finally {
      setFullLoader(false);
    }
  };

  const initValue = {
    clientName: mode === Modes.EDIT ? clientDetail?.clientName : "",
    clientDomain: mode === Modes.EDIT ? clientDetail?.clientDomain : "",
    orgGrowthStageId:
      mode === Modes.EDIT
        ? clientOrgs?.find(
            (el) => el.value === clientDetail?.orgGrowthStageId
          ) || ""
        : "",
    sourceIdList:
      mode === Modes.EDIT
        ? clientDetail?.sourceList?.map((el) => ({
            label: el.source,
            value: el.sourceId,
            allowMultipleMappings: el.allowMultipleMappings,
          }))
        : [],
    subSectorIdList:
      mode === Modes.EDIT
        ? processSubSectors(SubSectorType.SUBSECTOR, clientDetail)
        : [],
    functionIdList:
      mode === Modes.EDIT
        ? processSubSectors(SubSectorType.FUNCTIONS, clientDetail)
        : [],
    contentProviderList:
      mode === Modes.EDIT
        ? clientDetail?.contentProviderList?.map((el) => ({
            label: el.contentProvider,
            value: el.contentProviderId,
          }))
        : [],
    disprzInstanceName: mode === Modes.EDIT ? clientDetail?.disprzInstanceName : "",
    region:
      mode === Modes.EDIT && !!clientDetail?.region
        ? clientInstanceRegion?.find(
            (el) => el.value === Number(clientDetail?.region)
          )
        : "",
    proficiencySchemeName:
      mode === Modes.EDIT ? clientDetail?.proficiencySchemeName : "",
  };

  return {
    handleCheckBack,
    clientOrgs,
    initValue,
    formikRef,
    formSubmit,
    clientInstanceRegion,
    deleteTrigger,
    sourceMenu,
    handleSearch,
    removeChip,
    subSectors,
    handleSelectAll,
    functions,
    contentProviders,
  };
};

export default useClientManagement;
